import React, { useState, useEffect, useRef, forwardRef, useImperativeHandle } from 'react'
import cls from './Button.module.scss'

interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  children?: React.ReactNode
  buttonClass?: string
  buttonText?: string
  clickButton?: any
  removeDisableActions?: string[]
  defaultEmit?: boolean
  loading?: boolean
  variant?: 'primary' | 'secondary' | 'danger' | 'success'
}

export interface ButtonRef {
  focus: () => void
}

const Button = forwardRef<ButtonRef, ButtonProps>(
  (
    {
      children,
      buttonClass = '',
      buttonText,
      clickButton,
      removeDisableActions = [],
      defaultEmit = false,
      loading = false,
      variant = 'primary',
      disabled: propDisabled,
      onClick,
      className = '',
      ...props
    },
    ref,
  ) => {
    const [internalDisabled, setInternalDisabled] = useState(false)
    const buttonRef = useRef<HTMLButtonElement>(null)

    useImperativeHandle(ref, () => ({
      focus: () => {
        if (buttonRef.current) {
          buttonRef.current.focus()
        }
      },
    }))

    const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
      if (clickButton) {
        const shouldDisable = clickButton(e)
        if (shouldDisable === true) {
          setInternalDisabled(true)
        }
      }

      if (onClick) {
        onClick(e)
      }
    }

    const removeDisable = () => {
      setInternalDisabled(false)
    }

    useEffect(() => {
      if (internalDisabled) {
        const timer = setTimeout(() => {
          setInternalDisabled(false)
        }, 3000)

        return () => clearTimeout(timer)
      }
    }, [internalDisabled])

    useEffect(() => {
      const handleGlobalEvent = (event: CustomEvent) => {
        if (removeDisableActions.includes(event.detail?.type)) {
          removeDisable()
        }
      }

      if (removeDisableActions.length > 0) {
        window.addEventListener('custom-event', handleGlobalEvent as EventListener)
      }

      if (defaultEmit) {
        window.addEventListener('remove-common-button-disable', removeDisable)
      }

      return () => {
        if (removeDisableActions.length > 0) {
          window.removeEventListener('custom-event', handleGlobalEvent as EventListener)
        }
        if (defaultEmit) {
          window.removeEventListener('remove-common-button-disable', removeDisable)
        }
      }
    }, [removeDisableActions, defaultEmit])

    const isDisabled = propDisabled || internalDisabled || loading

    const getVariantClass = () => {
      switch (variant) {
        case 'primary':
          return cls.button
        case 'secondary':
          return cls.btnSecondary
        case 'danger':
          return cls.btnDanger
        case 'success':
          return cls.btnSuccess
        default:
          return cls.button
      }
    }

    const getDisabledClass = () => {
      return isDisabled ? 'disabled' : ''
    }

    const getLoadingClass = () => {
      return loading ? 'loading' : ''
    }

    const combinedClassName = [
      buttonClass,
      getVariantClass(),
      getDisabledClass(),
      getLoadingClass(),
      className,
    ]
      .filter(Boolean)
      .join(' ')

    return (
      <button
        ref={buttonRef}
        type={props.type || 'button'}
        onClick={handleClick}
        className={combinedClassName}
        disabled={isDisabled}
        {...props}
      >
        {children || buttonText}
      </button>
    )
  },
)

Button.displayName = 'Button'

export default Button